import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { container } from "../../infrastructure/container"
import { SessionService } from "../../infrastructure/services/session"
import { FranchiseService } from "../../infrastructure/services/api/franchise"
import { TYPES } from "../../presentation/models/types"
import { COLORS } from "../../presentation/constants/colors"
import { Logger } from "../../infrastructure/services/logger"
import { EnrolledDevice, Terminal } from "../../infrastructure/models/terminal"
import { ArrowLeft } from "lucide-react"
import { LockerStatus, TerminalStatus } from "../../infrastructure/models/transport/request/createterminal"
import { Duration } from "../../infrastructure/models/common/Duration"
import { SupportRole } from "../../infrastructure/models/common/supportrole"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import DeleteIcon from '@mui/icons-material/Delete'
import { PaymentType } from "../models/enum/PaymentType"

const TerminalsDetails: React.FC = () => {

    const franchiseService = container.resolve<FranchiseService>("franchiseService")
    const logger = container.resolve<Logger>("logger")
    const sessionService = container.resolve<SessionService>("sessionService")

    const { terminalId } = useParams<{ terminalId: string }>()
    const [terminalDetails, setTerminalDetails] = useState<Terminal | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)
    const [editMode, setEditMode] = useState<boolean>(false)
    const [editedFields, setEditedFields] = useState<Partial<Terminal>>({})
    const [alertMessage, setAlertMessage] = useState<string | null>(null)
    const [userRole] = useState<number | null>(sessionService?.user?.role ?? null)
    const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle")
    const navigate = useNavigate()
    const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false)
    const [successModalOpen, setSuccessModalOpen] = useState(false)
    const currentUser = sessionService.user
    const isSuperAdmin = currentUser?.role === SupportRole.SUPERADMIN
    const isAdmin = currentUser?.role === SupportRole.ADMIN
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [terminalToDelete, setTerminalToDelete] = useState<Terminal | null>(null)
    const [apiLoading, setApiLoading] = useState(false)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [confirmStage, setConfirmStage] = useState(false)
    const [devices, setDevices] = useState<EnrolledDevice[]>([])

    const lockerSizeDimensionMap = React.useMemo(() => {
        const map: Record<string, { width: number; height: number; depth: number }> = {}

        terminalDetails?.lockers?.forEach((locker) => {
            if (locker.size && locker.dimensions) {
                map[locker.size] = locker.dimensions
            }
        })

        return map
    }, [terminalDetails])

    const lockerSizes = React.useMemo(() => {
        return Object.keys(lockerSizeDimensionMap)
    }, [lockerSizeDimensionMap])

    const lockerStatusOptions = [
        TYPES.AVAILABLE,
        TYPES.INUSE,
        TYPES.MAINTENANCE,
    ]

    /** Fetch terminal details using franchiseService */
    useEffect(() => {
        const fetchTerminalDetails = async () => {
            setLoading(true)
            try {
                if (!terminalId) throw new Error("Invalid terminal ID.")
                logger.info("Fetching terminal details for:", terminalId)

                const response = await franchiseService.getTerminalDetails(terminalId)
                logger.info("API Response:", response)

                if (response) {
                    setTerminalDetails(response)
                } else {
                    throw new Error("No terminal details found.")
                }
            } catch (err) {
                logger.error("Error fetching terminal details:", err)
                setError(err instanceof Error ? err.message : "Error fetching details")
            } finally {
                setLoading(false)
            }
        }

        fetchTerminalDetails()
    }, [terminalId, franchiseService, logger])

    /** Toggles edit mode and resets edited fields */
    const handleEditToggle = () => {
        setEditMode((prev) => !prev)
        setEditedFields({})
    }

    /** Handles input changes for terminal details */
    const handleChange = (field: keyof Terminal, value: any) => {
        setEditedFields((prev) => ({
            ...prev,
            [field]: value === "" ? "" : value,
        }))
    }

    const handleVideoChange = (index: number, field: string, value: any) => {
        const existing = editedFields.videos ?? terminalDetails?.videos ?? []
        const updated = [...existing]

        updated[index] = {
            ...updated[index],
            [field]: field === "isActive" ? value === "true" : value
        }

        setEditedFields((prev) => ({
            ...prev,
            videos: updated
        }))
    }

    const handleNestedChange = (
        parentField: keyof Terminal,
        field: string,
        value: any,
        index?: number
    ) => {
        setEditedFields((prev) => {
            // Always merge with existing data (editedFields first, then fallback to terminalDetails)
            const parentValue = {
                ...(terminalDetails?.[parentField] as Record<string, any>),
                ...(prev[parentField] as Record<string, any>),
            }

            if (index !== undefined) {
                // For array-type fields like prices[]
                const arrayField = Array.isArray(parentValue[field])
                    ? [...parentValue[field]]
                    : []
                arrayField[index] = value
                parentValue[field] = arrayField
            } else {
                parentValue[field] = value
            }

            return {
                ...prev,
                [parentField]: parentValue,
                lockers: prev.lockers || terminalDetails?.lockers || [],
            }
        })
    }

    const handleClose = () => {
        if (editMode) {
            setEditMode(false) // exit edit mode
        } else {
            navigate(-1) // go back
        }
    }

    setTimeout(() => {
        setSaveStatus("idle")
    }, 3000)

    /** Handles changes for locker status */
    const handleLockerStatusChange = (lockerIndex: number, status: string) => {
        setEditedFields((prev) => {
            const updatedLockers = [
                ...(prev.lockers || terminalDetails?.lockers || []),
            ]

            updatedLockers[lockerIndex] = {
                ...updatedLockers[lockerIndex],
                status,
            }

            return {
                ...prev,
                lockers: updatedLockers,
            }
        })
    }

    if (loading)
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        )

    if (error)
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    <p
                        data-automation-id="terminaldetails-error-fetching-details-text"
                        id="terminaldetails-error-fetching-details-text"
                    >
                        {error}
                    </p>
                </div>
            </div>
        )

    if (!terminalDetails)
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    <p
                        data-automation-id="terminaldetails-details-not-avaiable-text"
                        id="terminaldetails-details-not-avaiable-text"
                    >
                        No terminal details found.
                    </p>
                </div>
            </div>
        )

    const mergeDeep = (target: any, source: any) => {
        for (const key in source) {
            if (
                source[key] &&
                typeof source[key] === "object" &&
                !Array.isArray(source[key])
            ) {
                target[key] = mergeDeep(target[key] ?? {}, source[key])
            } else {
                target[key] = source[key]
            }
        }
        return target
    }

    const handleConfirmDelete = async () => {
        if (!terminalDetails?.terminalId) return

        try {
            setLoading(true)
            await franchiseService.deleteTerminal(terminalDetails.terminalId)
            logger.info("Terminal deleted successfully")

            setDeleteConfirmOpen(false)
            setSuccessModalOpen(true)
            // Navigate back to franchise details page
            navigate("/dashboard")
            setTimeout(() => {
                setSuccessModalOpen(false)
                navigate("/dashboard") // go back
            }, 1500)
        } catch (error: any) {
            logger.error("Error deleting terminal:", error)
            alert(
                error?.response?.data?.message ||
                error.message ||
                "Failed to delete terminal"
            )
        } finally {
            setLoading(false)
        }
    }

    const handleSubmitUpdateTerminal = async () => {
        if (!terminalDetails?.terminalId) return

        const sanitizeStatus = (status: string): LockerStatus => {
            const mapping: Record<string, LockerStatus> = {
                available: LockerStatus.AVAILABLE,
                unavailable: LockerStatus.UNAVAILABLE,
                "in use": LockerStatus.IN_USE,
                in_use: LockerStatus.IN_USE,
                maintenance: LockerStatus.MAINTENANCE,
            }
            return mapping[status.toLowerCase()] ?? LockerStatus.AVAILABLE
        }

        const sanitizedLockers = (
            editedFields.lockers ?? terminalDetails.lockers ?? []
        ).map((locker) => ({
            ...locker,
            status: sanitizeStatus(locker.status),
        }))

        setLoading(true)

        try {
            //Start with current terminalDetails
            let updatePayload: Partial<Terminal> = { ...terminalDetails }

            //Deep merge editedFields so only changed values overwrite
            updatePayload = mergeDeep(updatePayload, editedFields)

            //Force some fields to always have correct values
            updatePayload.supportNumber =
                editedFields.supportNumber ?? terminalDetails.supportNumber ?? ""

            updatePayload.secondarySupportNumber =
                editedFields.secondarySupportNumber ??
                terminalDetails.secondarySupportNumber ??
                []

            // Apply sanitized lockers
            updatePayload.lockers = sanitizedLockers

            // Always keep IDs in payload
            updatePayload.terminalId = terminalDetails.terminalId
            updatePayload.franchiseId = terminalDetails.franchiseId

            console.log("Sending Terminal Update Payload:", updatePayload)

            updatePayload.basePrice = {
                ...terminalDetails.basePrice,
                ...editedFields.basePrice,
            }

            updatePayload.additionalPrices = {
                ...terminalDetails.additionalPrices,
                ...editedFields.additionalPrices,
            }

            // Convert time → number
            if (updatePayload.basePrice?.time !== undefined) {
                updatePayload.basePrice.time = Number(updatePayload.basePrice.time)
            }
            if (updatePayload.additionalPrices?.time !== undefined) {
                updatePayload.additionalPrices.time = Number(updatePayload.additionalPrices.time)
            }
            // Normalize duration enums
            if (updatePayload.basePrice?.duration) {
                updatePayload.basePrice.duration = updatePayload.basePrice.duration.toLowerCase()
            }
            if (updatePayload.additionalPrices?.duration) {
                updatePayload.additionalPrices.duration = updatePayload.additionalPrices.duration.toLowerCase()
            }

            const updatedTerminal = await franchiseService.updateTerminal(
                updatePayload as Terminal
            )

            setTerminalDetails(updatedTerminal)
            setEditedFields({})
            setEditMode(false)

            setUpdateSuccessOpen(true)
            setTimeout(() => {
                setUpdateSuccessOpen(false)
            }, 1000)
        } catch (error: any) {
            console.error("Error while updating terminal:", error)
            const message =
                error?.response?.data?.message ||
                error.message ||
                "Update failed"
            alert(`Update failed. ${message}`)
        } finally {
            setLoading(false)
        }
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const handleDeleteClick = () => {
        if (!confirmStage) {
            setConfirmStage(true) // first click sets confirm stage
            setTimeout(() => setConfirmStage(false), 2000) // reset after 2 seconds
            return
        }

        // Second click → actually open delete confirmation dialog
        setTerminalToDelete(terminalDetails)
        setDeleteConfirmOpen(true)
        setConfirmStage(false)
        handleMenuClose()
    }

    const handleLockerSizeChange = (index: number, newSize: string) => {
        setEditedFields((prev) => {
            const updatedLockers = [
                ...(prev.lockers || terminalDetails?.lockers || []),
            ]

            const existingDimensions =
                lockerSizeDimensionMap[newSize] ??
                updatedLockers[index].dimensions

            updatedLockers[index] = {
                ...updatedLockers[index],
                size: newSize,
                dimensions: existingDimensions,
            }

            return {
                ...prev,
                lockers: updatedLockers,
            }
        })
    }

    return (
        <div>
            {saveStatus === "saving" && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg">
                        <p
                            data-automation-id="terminaldetails-update-details-saving-text"
                            id="terminaldetails-update-details-saving-text"
                            className="text-lg font-semibold"
                        >
                            Saving changes...
                        </p>
                    </div>
                </div>
            )}

            {saveStatus === "success" && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                        {/* Tick Mark in Circle (Green Background, White Tick) */}
                        <div className="flex justify-center mb-4">
                            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                                <svg
                                    data-automation-id="terminaldetails-success-message-tickmark-img"
                                    id="terminaldetails-success-message-tickmark-img"
                                    className="w-8 h-8 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="3"
                                        d="M5 13l4 4L19 7"
                                    ></path>
                                </svg>
                            </div>
                        </div>

                        {/* Success Message (Below Tick Mark) */}
                        <p
                            data-automation-id="terminaldetails-successful-update-detail-terminal-message-text"
                            id="terminaldetails-successful-update-detail-terminal-message-text"
                            className="text-lg font-semibold break-words"
                        >
                            Terminal Details edited <br /> Successfully!
                        </p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div>
                <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 flex items-center">
                    <button
                        data-automation-id="terminaldetails-page-going-back-arrow-button"
                        id="terminaldetails-page-going-back-arrow-button"
                        onClick={handleClose}
                        className="flex items-center text-gray-700 hover:text-gray-900"
                    >
                        <ArrowLeft className="h-5 w-5 mr-1" />
                        {editMode ? "Back to Terminal Details" : "Back To All Terminals"}
                    </button>

                    {/* Spacer */}
                    <div className="flex items-center ml-auto gap-4">
                        {!editMode && (isSuperAdmin || isAdmin) && (
                            <button
                                data-automation-id="terminaldetails-form-edit-button"
                                id="terminaldetails-form-edit-button"
                                onClick={handleEditToggle}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded-md text-sm"
                            >
                                Edit
                            </button>
                        )}

                        {/** Delete button will appear in Edit Mode */}
                        {editMode && (
                            <Tooltip title="Actions" arrow>
                                <IconButton
                                    onClick={handleMenuOpen}
                                    sx={{
                                        backgroundColor: COLORS.ASH,
                                        borderRadius: "50%",
                                        "&:hover": {
                                            backgroundColor: COLORS.ASH,
                                            opacity: 0.9,
                                        },
                                    }}
                                >
                                    <MoreVertIcon sx={{ color: COLORS.BLACK }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        {isSuperAdmin && (
                            <MenuItem onClick={handleDeleteClick}>
                                <DeleteIcon style={{ color: COLORS.RED, marginRight: 8 }} />
                                {confirmStage ? "Click Again to Confirm" : "Delete Terminal"}
                            </MenuItem>
                        )}
                    </Menu>
                </div>

            </div>

            <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6">
                {!editMode ? (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                {/* Left column */}
                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-30">Name</span>
                                    <span className="text-gray-900 font-medium">:</span>
                                    <span className="text-gray-900 ml-2">
                                        {`Terminal - ${terminalDetails.name}`}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-30">Terminal Id</span>
                                    <span className="text-gray-900 font-medium">:</span>
                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.terminalId}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-30">Status</span>
                                    <span className="text-gray-900 font-medium">:</span>
                                    <span className="ml-2">
                                        <span
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                                            style={{
                                                backgroundColor:
                                                    terminalDetails.status?.toLowerCase() === TYPES.ACTIVE.toLowerCase()
                                                        ? COLORS.ACTIVE
                                                        : terminalDetails.status?.toLowerCase() === TYPES.INACTIVE.toLowerCase()
                                                            ? COLORS.RED
                                                            : COLORS.MAINTENANCE,
                                            }}
                                        >
                                            {terminalDetails.status}
                                        </span>
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-30">Address</span>
                                    <span className="text-gray-900 font-medium">:</span>
                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.address?.street},{" "}
                                        {terminalDetails.address?.city},{" "}
                                        {terminalDetails.address?.state}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-30">Latitude</span>
                                    <span className="text-gray-900 font-medium">:</span>
                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.latitude}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-30">Longitude</span>
                                    <span className="text-gray-900 font-medium">:</span>
                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.longitude}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-30">
                                        Base Price Duration
                                    </span>

                                    <span className="text-gray-900 font-medium">:</span>

                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.basePrice?.time} (
                                        {terminalDetails.basePrice?.duration})
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-30">
                                        Mac Address
                                    </span>

                                    <span className="text-gray-900 font-medium">:</span>

                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.bluetoothMacId}
                                    </span>
                                </div>

                                {terminalDetails.terminalType?.toLowerCase() === "freshkart" && (
                                    <div className="flex items-baseline">
                                        <span className="text-gray-500 w-30">Terminal Alias</span>

                                        <span className="text-gray-900 font-medium">:</span>

                                        <span className="text-gray-900 ml-2">
                                            {terminalDetails.alias}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-4">
                                {/* Right column */}
                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-48">
                                        Allowed Manuals sessions per month
                                    </span>

                                    <span className="text-gray-900 font-medium">:</span>

                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.allowedManualSessionsPerMonth}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-48">Support Number</span>

                                    <span className="text-gray-900 font-medium">:</span>

                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.supportNumber}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-48">Secondary Support Number</span>

                                    <span className="text-gray-900 font-medium">:</span>

                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.secondarySupportNumber?.map((support, index) => (
                                            <span key={support.name || index} className="block">
                                                {support.name} — {support.number}
                                            </span>
                                        ))}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-48">
                                        Drop payment required
                                    </span>

                                    <span className="text-gray-900 font-medium">:</span>

                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.dropPaymentRequired ? "Yes" : "No"}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-48">Terminal Type</span>

                                    <span className="text-gray-900 font-medium">:</span>

                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.terminalType}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-48">Payment Type</span>

                                    <span className="text-gray-900 font-medium">:</span>

                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.paymentType}
                                    </span>
                                </div>

                                <div className="flex items-baseline">
                                    <span className="text-gray-500 w-48">
                                        Additional Price Duration
                                    </span>

                                    <span className="text-gray-900 font-medium">:</span>

                                    <span className="text-gray-900 ml-2">
                                        {terminalDetails.additionalPrices?.time} (
                                        {terminalDetails.additionalPrices?.duration})
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            {/* Base Price */}
                            <div className="flex flex-col">
                                <span className="text-gray-700 font-medium">Base Price</span>
                                <div className="grid grid-cols-3 gap-4 mt-2">
                                    {terminalDetails.basePrice?.prices?.map((price, index) => (
                                        <div key={index} className="flex flex-col">
                                            <span className="text-xs text-gray-500">{price.size}</span>
                                            <span className="font-medium">Rs. {price.price}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Additional Price */}
                            <div className="flex flex-col">
                                <span className="text-gray-700 font-medium">Additional Price</span>
                                <div className="grid grid-cols-3 gap-4 mt-2">
                                    {terminalDetails.additionalPrices?.prices?.map((price, index) => (
                                        <div key={index} className="flex flex-col">
                                            <span className="text-xs text-gray-500">{price.size}</span>
                                            <span className="font-medium">Rs. {price.price}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>


                        <div className="mb-4 mt-4">
                            <h3 className="text-lg font-bold mb-2">Enrolled Device Details</h3>

                            {terminalDetails.enrolledDevice &&
                                terminalDetails.enrolledDevice.status?.toLowerCase() !== "unenrolled" ? (
                                <div className="grid gap-4">
                                    <div className="border rounded-lg p-4 bg-gray-50 shadow-sm">
                                        {/* Top Row: Device ID */}
                                        <div className="mb-2 font-bold text-gray-700">Device ID: {terminalDetails.enrolledDevice.deviceId}</div>

                                        {/* Second Row: 4-column layout */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm text-black-600">
                                            <div>
                                                <span className="font-semibold">User ID:</span> {terminalDetails.enrolledDevice.userId}
                                            </div>
                                            <div className="flex items-baseline">
                                                <span className="font-semibold">Provisioning Status</span>
                                                <span className="text-black-900 font-medium">:</span>
                                                <span className="ml-2">
                                                    <span
                                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                                                        style={{
                                                            backgroundColor:
                                                                terminalDetails.enrolledDevice?.status?.toLowerCase() === "provisioned"
                                                                    ? COLORS.ACTIVE
                                                                    : terminalDetails.enrolledDevice?.status?.toLowerCase() === "pending"
                                                                        ? COLORS.MAINTENANCE
                                                                        : terminalDetails.enrolledDevice?.status?.toLowerCase() === "revoked"
                                                                            ? COLORS.RED
                                                                            : COLORS.DEFAULT,
                                                        }}
                                                    >
                                                        {terminalDetails.enrolledDevice?.status}
                                                    </span>
                                                </span>
                                            </div>

                                            <div>
                                                <span className="font-semibold">Make / Model:</span> {terminalDetails.enrolledDevice.deviceMake} / {terminalDetails.enrolledDevice.deviceModel}
                                            </div>
                                            <div>
                                                <span className="font-semibold">OS Version:</span> {terminalDetails.enrolledDevice.osVersion}
                                            </div>
                                            <div>
                                                <span className="font-semibold">App Version:</span> {terminalDetails.enrolledDevice.appVersion}
                                            </div>
                                            <div>
                                                <span className="font-semibold">First Login:</span> {terminalDetails.enrolledDevice.firstLogin}
                                            </div>
                                            <div>
                                                <span className="font-semibold">Last Connection:</span> {terminalDetails.enrolledDevice.lastConnection}
                                            </div>
                                            <div>
                                                <span className="font-semibold">Client IP:</span> {terminalDetails.enrolledDevice.clientIp}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-2 p-4 border border-dashed border-gray-300 rounded-lg text-center bg-gray-50">
                                    <p className="text-black-900">No device enrolled for this terminal.</p>
                                </div>
                            )}
                        </div>

                        {/* Lockers Section */}
                        <div className="mt-8">
                            <h3
                                data-automation-id="terminaldetails-form-locker-section-label"
                                id="terminaldetails-form-locker-section-label"
                                className="text-lg font-medium text-gray-900 mb-4"
                            >
                                Lockers
                            </h3>

                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 overflow-y-auto max-h-96 text-center">
                                {terminalDetails.lockers?.map((locker, index) => (
                                    <div
                                        key={index}
                                        className="border rounded-lg overflow-hidden bg-gray-200 aspect-square"
                                    >
                                        <div className="p-3 border-b">
                                            {/* Locker Number */}
                                            <h4
                                                data-automation-id="terminaldetails-locker-details-lockernumber-text"
                                                id="terminaldetails-locker-details-lockernumber-text"
                                                className="font-bold text-xs"
                                            >
                                                {" "}
                                                {/* Reduced to text-xs */}
                                                Locker {locker.lockerNumber}
                                            </h4>

                                            {/* Locker Size and Dimensions */}
                                            <p className="text-xs">
                                                {" "}
                                                {/* Reduced to text-xs */}
                                                <span className="font-bold text-black">
                                                    {locker.size}
                                                </span>{" "}

                                                <span className="text-gray-500">
                                                    : {locker.dimensions.width}*{locker.dimensions.height}
                                                    *{locker.dimensions.depth}
                                                </span>
                                            </p>

                                            {/* Locker Status Badge */}
                                            <span
                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-2xs font-medium"
                                                style={{
                                                    backgroundColor:
                                                        locker.status.toLowerCase() === "in use"
                                                            ? COLORS.INACTIVE
                                                            : locker.status.toLowerCase() === "available"
                                                                ? COLORS.ACTIVE
                                                                : locker.status.toLowerCase() === "maintenance"
                                                                    ? COLORS.MAINTENANCE
                                                                    : COLORS.DEFAULT,
                                                }}
                                            >
                                                {locker.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (

                    <Box sx={{ backgroundColor: "#fff", borderRadius: 2, p: 4, boxShadow: 1 }}>
                        <Grid container spacing={3} mb={4}>
                            {/* Terminal Name */}
                            <Grid size={{ xs: 4, sm: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Name"
                                    value={editedFields.name ?? terminalDetails.name ?? ""}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                />
                            </Grid>

                            {/* Terminal Status */}
                            <Grid size={{ xs: 4, sm: 4 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Status"
                                    value={editedFields.status || terminalDetails.status}
                                    onChange={(e) => handleChange("status", e.target.value)}
                                >
                                    {Object.values(TerminalStatus).map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid size={{ xs: 4, sm: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Support Number"
                                    value={
                                        // Show 10 digits only, stripping +91 if present
                                        (editedFields.supportNumber ?? terminalDetails.supportNumber ?? "").replace("+91", "")
                                    }
                                    onChange={(e) => {
                                        // Keep only digits, max 10
                                        const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10)
                                        // Call handleChange with +91 prefixed for API
                                        handleChange("supportNumber", `+91${onlyDigits}`)
                                    }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                                    }}
                                    inputProps={{
                                        maxLength: 10,
                                        pattern: "\\d{10}",
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {/* Conditional UI */}
                        <Grid container spacing={3} mb={4}>
                            {terminalDetails.terminalType?.toLowerCase() === "freshkart" && (
                                <Grid size={{ xs: 6, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Alias"
                                        value={editedFields.alias ?? terminalDetails.alias ?? ""}
                                        onChange={(e) => handleChange("alias", e.target.value)}
                                    />
                                </Grid>
                            )}

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Collect Dead Line"
                                    value={editedFields.collectDeadline ?? terminalDetails.collectDeadline ?? ""}
                                    onChange={(e) => handleChange("collectDeadline", e.target.value)}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} mb={4}>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <Grid container alignItems="center" justifyContent="space-between">
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Secondary Support Numbers
                                    </Typography>

                                    <Tooltip title="Click Here to Add Secondary Support Numbers with Names." arrow>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    const existing =
                                                        editedFields.secondarySupportNumber ??
                                                        terminalDetails.secondarySupportNumber ??
                                                        []
                                                    const updated = [...existing, { name: "", number: "" }]
                                                    setEditedFields((prev) => ({
                                                        ...prev,
                                                        secondarySupportNumber: updated,
                                                    }))
                                                }}
                                            >
                                                Add
                                            </Button>
                                        </Typography>
                                    </Tooltip>
                                </Grid>
                            </Grid>

                            {(
                                editedFields.secondarySupportNumber ??
                                terminalDetails.secondarySupportNumber ??
                                []
                            ).map((item, index) => {
                                // helper to update safely
                                const updateSecondarySupport = (
                                    index: number,
                                    key: "name" | "number",
                                    value: string
                                ) => {
                                    const existing =
                                        editedFields.secondarySupportNumber ??
                                        terminalDetails.secondarySupportNumber ??
                                        []
                                    const updated = [...existing]
                                    updated[index] = { ...updated[index], [key]: value }

                                    setEditedFields((prev) => ({
                                        ...prev,
                                        secondarySupportNumber: updated,
                                    }))
                                }

                                return (
                                    <Grid size={{ xs: 12, sm: 12 }} key={index}>
                                        <Grid container spacing={1} alignItems="center">
                                            {/* Name Field */}
                                            <Grid size={{ xs: 4, sm: 4 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Name"
                                                    value={item?.name ?? ""}
                                                    onChange={(e) => updateSecondarySupport(index, "name", e.target.value)}
                                                />
                                            </Grid>

                                            {/* Number Field */}
                                            <Grid size={{ xs: 4, sm: 4 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Number"
                                                    value={(item?.number ?? "").replace(/^\+91/, "")}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, "")
                                                        if (value.length > 10) value = value.slice(0, 10)
                                                        updateSecondarySupport(index, "number", `+91${value}`)
                                                    }}
                                                    InputProps={{
                                                        startAdornment: <span style={{ marginRight: 4 }}>+91</span>,
                                                    }}
                                                />
                                            </Grid>

                                            {/* Remove Button */}
                                            <Grid size={{ xs: 4, sm: 4 }} container justifyContent="flex-end">
                                                <Button
                                                    color="error"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        const existing =
                                                            editedFields.secondarySupportNumber ??
                                                            terminalDetails.secondarySupportNumber ??
                                                            []
                                                        const updated = [...existing]
                                                        updated.splice(index, 1)
                                                        setEditedFields((prev) => ({
                                                            ...prev,
                                                            secondarySupportNumber: updated,
                                                        }))
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )
                            })}
                        </Grid>

                        {/* Base Price Section */}
                        <Grid container spacing={3} mb={4}>
                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Base Price Time"
                                    value={editedFields.basePrice?.time ?? terminalDetails.basePrice?.time ?? ""}
                                    onChange={(e) => handleNestedChange("basePrice", "time", e.target.value)}
                                    onKeyDown={(e) => {
                                        const allowedKeys = [
                                            'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
                                        ]
                                        if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                                            e.preventDefault()
                                        }
                                    }}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Base Price Duration"
                                    value={editedFields.basePrice?.duration ?? terminalDetails.basePrice?.duration ?? ""}
                                    onChange={(e) =>
                                        handleNestedChange("basePrice", "duration", e.target.value)
                                    }
                                >
                                    {Object.values(Duration).map((d) => (
                                        <MenuItem key={d} value={d}>
                                            {d.charAt(0).toUpperCase() + d.slice(1)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>

                        {/* Additional Price Section */}
                        <Grid container spacing={3} mb={4}>
                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Additional Price Time"
                                    value={editedFields.additionalPrices?.time ?? terminalDetails.additionalPrices?.time ?? ""}
                                    onChange={(e) => handleNestedChange("additionalPrices", "time", e.target.value)}
                                    onKeyDown={(e) => {
                                        const allowedKeys = [
                                            'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
                                        ]
                                        if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                                            e.preventDefault()
                                        }
                                    }}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Additional Price Duration"
                                    name="additionalPrices.duration"
                                    value={editedFields.additionalPrices?.duration ?? terminalDetails.additionalPrices?.duration ?? ""}
                                    onChange={(e) =>
                                        handleNestedChange("additionalPrices", "duration", e.target.value)
                                    }
                                >
                                    {Object.values(Duration).map((d) => (
                                        <MenuItem key={d} value={d}>
                                            {d.charAt(0).toUpperCase() + d.slice(1)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>

                        {/* Base Price Breakdown */}
                        <Grid container spacing={2} mb={4}>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <Typography variant="subtitle1" fontWeight="bold">Base Prices (₹)</Typography>
                            </Grid>

                            {terminalDetails.basePrice?.prices.map((price, index) => (
                                <Grid size={{ xs: 6, sm: 3 }} key={index}>
                                    <TextField
                                        fullWidth
                                        label={price.size}
                                        value={
                                            editedFields.basePrice?.prices?.[index]?.price ??
                                            price.price ??
                                            ""
                                        }
                                        onChange={(e) =>
                                            handleNestedChange(
                                                "basePrice",
                                                "prices",
                                                { ...price, price: parseFloat(e.target.value) || 0 },
                                                index
                                            )
                                        }
                                        onKeyDown={(e) => {
                                            const allowedKeys = [
                                                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
                                            ]
                                            if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                                                e.preventDefault()
                                            }
                                        }}
                                        inputProps={{ inputMode: 'numeric' }}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        {/* Additional Price Breakdown */}
                        <Grid container spacing={2} mb={4}>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <Typography variant="subtitle1" fontWeight="bold">Additional Prices (₹)</Typography>
                            </Grid>

                            {terminalDetails.additionalPrices?.prices.map((price, index) => (
                                <Grid size={{ xs: 6, sm: 3 }} key={index}>
                                    <TextField
                                        fullWidth
                                        label={price.size}
                                        value={
                                            editedFields.additionalPrices?.prices?.[index]?.price ??
                                            price.price ??
                                            ""
                                        }
                                        onChange={(e) =>
                                            handleNestedChange(
                                                "additionalPrices",
                                                "prices",
                                                { ...price, price: parseFloat(e.target.value) || 0 },
                                                index
                                            )
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        <Grid container spacing={2} mb={4}>
                            {/* Payment Type (Left) */}
                            <Grid size={{ xs: 6, sm: 6 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Payment Type</Typography>
                                <TextField
                                    select
                                    fullWidth
                                    label="Payment Type"
                                    required
                                    value={editedFields.paymentType ?? terminalDetails?.paymentType ?? ""}
                                    onChange={(e) => handleChange("paymentType", e.target.value as PaymentType)}
                                >
                                    {Object.values(PaymentType).map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* MAC ID (Right) */}
                            <Grid size={{ xs: 6, sm: 6 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    MAC ID
                                </Typography>
                                <TextField
                                    fullWidth
                                    label="Bluetooth MAC ID"
                                    name="bluetoothMacId"
                                    value={editedFields.bluetoothMacId ?? terminalDetails.bluetoothMacId ?? ""}
                                    onChange={(e) => handleChange("bluetoothMacId", e.target.value)}
                                />
                            </Grid>
                        </Grid>

                        {/* Lockers Section */}
                        <Box mb={4}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Lockers
                            </Typography>

                            <Grid container spacing={1}>
                                {terminalDetails.lockers?.map((locker, index) => {
                                    const currentLocker = editedFields.lockers?.[index] ?? locker
                                    return (
                                        <Grid size={{ xs: 6, sm: 4, md: 2 }} key={index}>
                                            <Box
                                                border={1}
                                                borderColor="grey.300"
                                                borderRadius={2}
                                                p={2}
                                                textAlign="center"
                                                sx={{ backgroundColor: "#f0f0f0" }}
                                            >
                                                <Typography variant="body2" fontWeight="bold"> Locker {locker.lockerNumber} </Typography>

                                                {/* Size Dropdown */}
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    label="Size"
                                                    value={currentLocker.size}
                                                    onChange={(e) =>
                                                        handleLockerSizeChange(index, e.target.value)
                                                    }
                                                    sx={{ mt: 1 }}
                                                >
                                                    {lockerSizes.map((size) => (<MenuItem key={size} value={size}> {size} </MenuItem>))}
                                                </TextField>

                                                {/* Dimensions auto-updated from backend */}
                                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                    {currentLocker.dimensions.width} x {currentLocker.dimensions.height} x {currentLocker.dimensions.depth}
                                                </Typography>

                                                {/* Status */}
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    value={currentLocker.status}
                                                    onChange={(e) => handleLockerStatusChange(index, e.target.value)}
                                                    sx={{ mt: 1 }}
                                                >
                                                    {lockerStatusOptions.map((status) => (<MenuItem key={status} value={status}> {status} </MenuItem>))}
                                                </TextField>
                                            </Box>
                                        </Grid>
                                    )
                                })}
                            </Grid>
                        </Box>

                        <Grid container spacing={3} mb={4}>
                            {/* SMS Templates */}
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <span className="text-gray-700 font-medium">SMS Templates</span>
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="User Login OTP"
                                    value={editedFields.smsTemplates?.userLoginOtp ?? terminalDetails.smsTemplates?.userLoginOtp ?? ""}
                                    onChange={(e) =>
                                        handleChange("smsTemplates", {
                                            ...editedFields.smsTemplates,
                                            userLoginOtp: e.target.value,
                                        })
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Collect PIN"
                                    value={editedFields.smsTemplates?.collectPin ?? terminalDetails.smsTemplates?.collectPin ?? ""}
                                    onChange={(e) =>
                                        handleChange("smsTemplates", {
                                            ...editedFields.smsTemplates,
                                            collectPin: e.target.value,
                                        })
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Drop Off Acknowledgement"
                                    value={editedFields.smsTemplates?.dropOffAcknowledgement ?? terminalDetails.smsTemplates?.dropOffAcknowledgement ?? ""}
                                    onChange={(e) =>
                                        handleChange("smsTemplates", {
                                            ...editedFields.smsTemplates,
                                            dropOffAcknowledgement: e.target.value,
                                        })
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Subscription Pin Update"
                                    value={editedFields.smsTemplates?.subscriptionPinUpdate ?? terminalDetails.smsTemplates?.subscriptionPinUpdate ?? ""}
                                    onChange={(e) =>
                                        handleChange("smsTemplates", {
                                            ...editedFields.smsTemplates,
                                            subscriptionPinUpdate: e.target.value,
                                        })
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Resend Collect Pin"
                                    value={editedFields.smsTemplates?.resendCollectPin ?? terminalDetails.smsTemplates?.resendCollectPin ?? ""}
                                    onChange={(e) =>
                                        handleChange("smsTemplates", {
                                            ...editedFields.smsTemplates,
                                            resendCollectPin: e.target.value,
                                        })
                                    }
                                />
                            </Grid>

                            {/* Email Templates */}
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <span className="text-gray-700 font-medium">Email Templates</span>
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="User Login OTP"
                                    value={editedFields.emailTemplates?.userLoginOtp ?? terminalDetails.emailTemplates?.userLoginOtp ?? ""}
                                    onChange={(e) =>
                                        handleChange("emailTemplates", {
                                            ...editedFields.emailTemplates,
                                            userLoginOtp: e.target.value,
                                        })
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Request Collect PIN"
                                    value={editedFields.emailTemplates?.requestCollectPin ?? terminalDetails.emailTemplates?.requestCollectPin ?? ""}
                                    onChange={(e) =>
                                        handleChange("emailTemplates", {
                                            ...editedFields.emailTemplates,
                                            requestCollectPin: e.target.value,
                                        })
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Vendor Login Welcome"
                                    value={editedFields.emailTemplates?.vendorLoginWelcome ?? terminalDetails.emailTemplates?.vendorLoginWelcome ?? ""}
                                    onChange={(e) =>
                                        handleChange("emailTemplates", {
                                            ...editedFields.emailTemplates,
                                            vendorLoginWelcome: e.target.value,
                                        })
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Vendor Send Password"
                                    value={editedFields.emailTemplates?.vendorSendPassword ?? terminalDetails.emailTemplates?.vendorSendPassword ?? ""}
                                    onChange={(e) =>
                                        handleChange("emailTemplates", {
                                            ...editedFields.emailTemplates,
                                            vendorSendPassword: e.target.value,
                                        })
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Support User Welcome"
                                    value={editedFields.emailTemplates?.supportUserSendWelcome ?? terminalDetails.emailTemplates?.supportUserSendWelcome ?? ""}
                                    onChange={(e) =>
                                        handleChange("emailTemplates", {
                                            ...editedFields.emailTemplates,
                                            supportUserSendWelcome: e.target.value,
                                        })
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Support User Welcome"
                                    value={editedFields.emailTemplates?.subscriptionPinUpdate ?? terminalDetails.emailTemplates?.subscriptionPinUpdate ?? ""}
                                    onChange={(e) =>
                                        handleChange("emailTemplates", {
                                            ...editedFields.emailTemplates,
                                            subscriptionPinUpdate: e.target.value,
                                        })
                                    }
                                />
                            </Grid>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Typography variant="h6">Videos</Typography>
                        </Grid>

                        {(editedFields.videos ?? terminalDetails.videos ?? []).map((video, index) => (
                            <Grid
                                container
                                spacing={2}
                                alignItems="center"
                                key={index}
                                sx={{ mb: 2 }}
                            >
                                {/* Video URL */}
                                <Grid size={{ xs: 6, sm: 5 }}>
                                    <TextField
                                        fullWidth
                                        label="Video URL"
                                        value={video.source}
                                        onChange={(e) =>
                                            handleVideoChange(index, "source", e.target.value)
                                        }
                                    />
                                </Grid>

                                {/* Description */}
                                <Grid size={{ xs: 6, sm: 5 }}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        value={video.description}
                                        onChange={(e) =>
                                            handleVideoChange(index, "description", e.target.value)
                                        }
                                    />
                                </Grid>

                                {/* Active */}
                                <Grid size={{ xs: 4, sm: 1 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Active"
                                        value={video.isActive ? "true" : "false"}
                                        onChange={(e) =>
                                            handleVideoChange(index, "isActive", e.target.value)
                                        }
                                    >
                                        <MenuItem value="true">Yes</MenuItem>
                                        <MenuItem value="false">No</MenuItem>
                                    </TextField>
                                </Grid>

                                {/* Remove Button */}
                                <Grid size={{ xs: 4, sm: 1 }}>
                                    <Button
                                        fullWidth
                                        color="error"
                                        variant="outlined"
                                        sx={{ height: "56px" }} // align with TextField height
                                        onClick={() => {
                                            const existing =
                                                editedFields.videos ?? terminalDetails.videos ?? []
                                            const updated = [...existing]
                                            updated.splice(index, 1)

                                            setEditedFields((prev) => ({
                                                ...prev,
                                                videos: updated,
                                            }))
                                        }}
                                    >
                                        X
                                    </Button>
                                </Grid>
                            </Grid>
                        ))}

                        <Grid size={{ xs: 12 }}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    const existing = editedFields.videos ?? terminalDetails.videos ?? []
                                    setEditedFields((prev) => ({
                                        ...prev,
                                        videos: [...existing, { source: "", description: "", isActive: true }]
                                    }))
                                }}
                            >
                                Add Video
                            </Button>
                        </Grid>

                        {/* Action Buttons */}
                        <Box display="flex" justifyContent="flex-end" gap={2}>
                            <Button onClick={handleEditToggle} variant="outlined"> Cancel </Button>

                            <Button variant="contained" color="primary" onClick={handleSubmitUpdateTerminal} > {loading ? "Saving..." : "Save Changes"} </Button>
                        </Box>
                    </Box>
                )}

                {alertMessage && (
                    <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                        {alertMessage}
                    </div>
                )}

                <Dialog
                    open={updateSuccessOpen}
                    onClose={() => setUpdateSuccessOpen(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                        <div
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "64px",
                                height: "64px",
                                borderRadius: "50%",
                                backgroundColor: "#4CAF50",
                                margin: "0 auto 16px auto"
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-8 h-8"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="white"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>

                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Terminal Details Updated Successfully.
                        </Typography>
                    </DialogContent>
                </Dialog>

                <Dialog
                    open={deleteConfirmOpen}
                    onClose={() => {
                        setDeleteConfirmOpen(false)
                        setTerminalToDelete(null)
                    }}
                >
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <p>Are you sure you want to delete the terminal "<strong>{terminalToDelete?.name}</strong>"?</p>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            onClick={() => {
                                setDeleteConfirmOpen(false)
                                setTerminalToDelete(null)
                            }}
                            color="secondary"
                        >
                            Cancel
                        </Button>

                        <Button onClick={handleConfirmDelete} color="error" disabled={apiLoading}>
                            {apiLoading ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={successModalOpen}
                    onClose={() => setSuccessModalOpen(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                        <div
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "64px",
                                height: "64px",
                                borderRadius: "50%",
                                backgroundColor: COLORS.LIGHT_GREEN,
                                margin: "0 auto 16px auto",
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-8 h-8"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="white"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>

                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Terminal deleted successfully.
                        </Typography>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    )
}

export default TerminalsDetails